<template>
  <div class="intro">
    <div class="intro-body l-container">
      <div :class="['search-wrapper', { focus }]">
        <h1 class="heading-l">{{ header }}</h1>
        <p class="body-s-regular">{{ text }}</p>
        <label
          v-on-click-outside="() => setFocus(false)"
          :class="['input-wrapper', { focus }]"
        >
          <div class="input">
            <div
              :class="['search-hint', { show: focus && search && !loading }]"
            >
              <span class="hide-l">{{ search }}</span>
              <span class="hint">{{ inputHint }}</span>
            </div>
            <form action=".">
              <input
                ref="inputPromo"
                :value="search"
                :class="[{ focus }, 'heading-xs']"
                type="search"
                :placeholder="placeholder"
                @focus="setFocus(true)"
                @click="setFocus(true)"
                @input="onInput"
                @keydown.enter.stop.prevent="searchCurrent"
                @keydown.arrow-up.prevent="setActiveHint('up')"
                @keydown.arrow-down.prevent="setActiveHint('down')"
                @keydown.tab.prevent="setActiveHint('down')"
                @keydown.arrow-right.stop="completeText"
              />
            </form>
          </div>

          <div
            v-if="hasMinimumLetters && focus && !loading"
            class="search-result"
            @click="$emit('toggleVisibleCatalog')"
          >
            <ul v-if="results.length">
              <li v-for="(result, index) in results" :key="index">
                <nuxt-link :to="`/${result[slugParam]}/`">
                  <div class="text-result">
                    {{ result.startString
                    }}<span class="hint">{{ result.searchInString }}</span
                    >{{ result.endString }}
                  </div>
                </nuxt-link>
              </li>
            </ul>
            <div v-else class="empty">
              {{ emptyStart }} с похожим названием не найдено, попробуйте
              изменить запрос
            </div>
          </div>
        </label>
      </div>
      <div :class="['bg', { show: focus }]" />
      <img class="image" :src="image" draggable="false" alt />
    </div>
  </div>
</template>

<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components';
import type { inputHintSuggest, resultIntroSearchItem } from '~/interfaces/app';

const emit = defineEmits(['change', 'clear', 'toggleVisibleCatalog']);

const props = defineProps({
  header: {
    type: String,
    required: true,
  },
  text: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    required: true,
  },
  image: {
    type: String,
    required: true,
  },
  emptyStart: {
    type: String,
    required: true,
  },
  data: {
    type: Array as () => resultIntroSearchItem[],
    required: true,
  },
  searchStart: {
    type: Number,
    default: 0,
  },
  slugParam: {
    type: String,
    default: 'slug',
  },
  nameParam: {
    type: String,
    default: 'name',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  requestUrl: {
    type: String,
    default: '',
  },
});
const router = useRouter();
const focus = ref(false);
const search = ref('');
const activeHintIndex = ref<number>(-1);
const inputPromo = ref<HTMLElement>();

// computed

const results = computed<inputHintSuggest[]>(() => {
  const res: inputHintSuggest[] = props.data.slice(0, 5).map((el) => ({
    ...el,
    ...splitString(el?.name),
  }));
  return res;
});

const hasMinimumLetters = computed<Boolean>(
  () => search.value?.length > props.searchStart
);

const inputHint = computed<String>(() => {
  if (!getActive.value?.name) return '';
  const res: inputHintSuggest = splitString(
    replaceSpacesToNonBreak(getActive.value?.name)
  );
  return res.endString;
});

const getActive = computed<any>(
  () => results.value[activeHintIndex.value === -1 ? 0 : activeHintIndex.value]
);

// methods
const onInput = (e: Event) => {
  // @ts-ignore
  const inputValue = e.target?.value || '';
  search.value = inputValue;
  activeHintIndex.value = -1;
  emit('change', inputValue);
};

const setActiveHint = (type: String) => {
  const currentIndex = activeHintIndex.value;
  const resultsLength = results.value.length;
  if (type === 'down') {
    activeHintIndex.value =
      currentIndex >= resultsLength - 1 ? 0 : currentIndex + 1;
  }
  if (type === 'up') {
    activeHintIndex.value =
      currentIndex === 0 ? resultsLength - 1 : currentIndex - 1;
  }
  if (getActive.value?.name) {
    search.value = getActive.value?.name;
  }
};

const replaceSpacesToNonBreak = (v: String = '') => v.replace(' ', '\u00A0');

const completeText = () => {
  if (activeHintIndex.value === -1) {
    activeHintIndex.value = 0;
  }
  search.value = getActive.value?.name || '';
};

const searchCurrent = () => {
  getActive.value &&
    getActive.value[props.slugParam as keyof resultIntroSearchItem] &&
    router?.push(
      `/${getActive.value[props.slugParam as keyof resultIntroSearchItem]}/`
    );
  clearAll();
};

const clearAll = () => {
  inputPromo.value?.blur();
  focus.value = false;
  activeHintIndex.value = -1;
  search.value = '';
  emit('clear');
};
const setFocus = (v: boolean) => (focus.value = v);

const splitString = (str: string): inputHintSuggest => {
  if (!str) return { startString: str, endString: '', searchInString: '' };
  const arr = str.toLowerCase().split(search.value.toLowerCase());
  if (arr.length === 1)
    return { startString: str, endString: '', searchInString: '' };
  const getOriginalCase = (subStr: string) => {
    const index = str.toLowerCase().indexOf(subStr.toLowerCase());
    if (index === -1) return '';
    return [...str].splice(index, subStr.length).join('');
  };
  const startString = getOriginalCase(arr.shift() || '');
  const endString = getOriginalCase(arr.join(''));
  const searchInString = getOriginalCase(search.value);
  return { startString, endString, searchInString };
};
</script>
<style scoped lang="scss" src="./IntroSearchScss.scss" />
